import { useStore } from '@nanostores/react';
import { useState, type PropsWithChildren } from 'react';
import { Button } from './Button';
import { clientFetch } from '~/lib/.client/fetch';
import { MSG_DEPLOY } from '~/lib/constants';
import { importStore } from '~/lib/projects';
import { chatStore } from '~/lib/stores/chat';
import { projectStore } from '~/lib/stores/project';
import { invariant } from '~/utils/invariant';
import { withSpinner } from '~/utils/withSpinner';

export type StaticHostingSetupAction = 'publish' | 'migrate';

interface StaticHostingSetupProps {
  action: StaticHostingSetupAction;
}

export const StaticHostingSetup = ({ children, action }: PropsWithChildren<StaticHostingSetupProps>) => {
  const [isLoading, setIsLoading] = useState(false);

  const hostingAction = useStaticHostingActions(action);

  const onClick = () => {
    setIsLoading(true);

    withSpinner({
      action: async () => {
        hostingAction?.run?.();
      },
      onStateUpdate: setIsLoading,
    });
  };

  return (
    <>
      <Button
        size="md"
        variant="primary"
        className="my-2"
        loading={isLoading}
        disabled={hostingAction?.isDisabled?.()}
        onClick={onClick}
      >
        {children}
      </Button>
    </>
  );
};

function useStaticHostingActions(action: 'publish' | 'migrate'):
  | {
      isDisabled?: () => boolean;
      run: () => Promise<void>;
    }
  | undefined {
  const { deploymentInfo } = useStore(importStore);
  const projectId = useStore(projectStore.projectId);

  const [isMigrating, setIsMigrating] = useState(false);

  const publish = {
    run: async () => {
      chatStore.events.dispatch('sendChatMessage', { message: MSG_DEPLOY, mode: 'build' });
    },
  };

  const migrate = {
    isDisabled: () => isMigrating || !deploymentInfo || deploymentInfo.kind === 'static-hosting',
    run: async () => {
      invariant(projectId);

      try {
        setIsMigrating(true);

        const response = await clientFetch(`/api/deploy/${projectId}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          // TODO update deploymentInfo in the store
          importStore.setKey('deploymentInfo', undefined);

          publish.run();
        }
      } finally {
        setIsMigrating(false);
      }
    },
  };

  switch (action) {
    case 'publish': {
      return publish;
    }
    case 'migrate': {
      return migrate;
    }
    default: {
      return undefined;
    }
  }
}
